import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { ChartWrapperInstance, ChartWrapperService } from '../../shared-services/chart-wrapper.service';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';

@Component({
  selector: 'app-quad-bubble-legend',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './quad-bubble-legend.component.html',
  styleUrl: './quad-bubble-legend.component.scss',
})
export class QuadBubbleLegendComponent {
  @Input() data: any;

  private readonly globalFilterService = inject(GlobalFilterService);
  private readonly configService = inject(ConfigService);
  private readonly chartWrapperService = inject(ChartWrapperService);
  private readonly wrapper: ChartWrapperInstance = this.chartWrapperService.createInstance();
  private dataSub?: Subscription;
  private lastFilterLoadSignature = '';
  private applicationMetricMap: Record<string, any> = {};

  ngOnInit(): void {
    const initialFilters = this.wrapper.attach(this.data, {
      onReload: (filters) => this.loadMappedMetrics(filters),
      getChart: () => null,
      getSeriesNames: () => [],
      getCategories: () => [],
    });

    this.loadMappedMetrics(initialFilters || {});
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].firstChange) {
      this.wrapper.updateData(this.data);
      this.loadMappedMetrics();
    }
  }

  ngOnDestroy(): void {
    this.dataSub?.unsubscribe();
    this.wrapper.detach();
  }

  get isDeepdiveHardcodedPanel(): boolean {
    return String(this.data?.mode || '').toLowerCase() === 'deepdive-hardcoded';
  }

  get mappedPriceIncrease(): string {
    const filters = this.globalFilterService.getCurrentFilters();
    const raw =
      filters?.requestParams?.['price_increase'] ??
      filters?.queryParams?.['price_increase'];
    const value = Array.isArray(raw) ? raw[0] : raw;
    const num = Number(value);
    if (!Number.isFinite(num)) return '0%';
    return `${num}%`;
  }

  get legendItems(): Array<{ title: string; color?: string; description?: string }> {
    if (Array.isArray(this.data?.legendItems) && this.data.legendItems.length) {
      return this.data.legendItems;
    }
    return [
      { title: 'Q1-MFN/Star', color: '#eef2db' },
      { title: 'Q2-Strategic/target', color: '#f1f5f9' },
      { title: 'Q3-Review/others', color: '#f8d7da' },
      { title: 'Q4-Key accounts', color: '#bae6fd' },
    ];
  }

  get sections(): Array<{
    title: string;
    items: Array<{ title: string; color?: string; description?: string }>;
    showBubbleScale?: boolean;
  }> {
    return Array.isArray(this.data?.sections) ? this.data.sections : [];
  }

  getConsPriceIncrease(): string {
    return this.getMetricValue('CONSTRUCTION', 'avg_price_increase', 'percent');
  }

  getAgriPriceIncrease(): string {
    return this.getMetricValue('AGRICULTURE', 'avg_price_increase', 'percent');
  }

  getConsSales(): string {
    return this.getMetricValue('CONSTRUCTION', 'total_sales', 'currencyCompact');
  }

  getAgriSales(): string {
    return this.getMetricValue('AGRICULTURE', 'total_sales', 'currencyCompact');
  }

  private loadMappedMetrics(filters: any = {}): void {
    if (!this.isDeepdiveHardcodedPanel || !this.data?.apiUrl) {
      return;
    }

    const filterLoadSignature = this.getFilterLoadSignature(filters);
    if (filters?.widgetId && filterLoadSignature === this.lastFilterLoadSignature) {
      return;
    }
    this.lastFilterLoadSignature = filterLoadSignature;

    this.dataSub?.unsubscribe();
    this.dataSub = this.configService
      .getConfig(this.data.apiUrl, this.data, filters, this.data.method)
      .subscribe({
        next: (response) => {
          this.applicationMetricMap = this.buildApplicationMetricMap(response);
        },
        error: () => {
          this.applicationMetricMap = {};
        },
      });
  }

  private buildApplicationMetricMap(response: any): Record<string, any> {
    const rows = this.resolveResponseRows(response);
    return rows.reduce((acc: Record<string, any>, row: any) => {
      const application = String(row?.application || '').trim().toUpperCase();
      if (application) {
        acc[application] = row;
      }
      return acc;
    }, {});
  }

  private resolveResponseRows(source: any): any[] {
    const responsePath = String(this.data?.responsePath || 'key_cons').trim();
    const resolved = responsePath
      ? responsePath.split('.').reduce((current: any, segment: string) => current?.[segment], source)
      : source;

    if (Array.isArray(resolved)) {
      return resolved;
    }

    if (Array.isArray(source?.key_cons)) {
      return source.key_cons;
    }

    return [];
  }

  private getMetricValue(application: string, field: string, format: 'percent' | 'currencyCompact'): string {
    const row = this.applicationMetricMap[String(application || '').trim().toUpperCase()];
    const value = row?.[field];

    if (value === null || value === undefined || value === '') {
      return format === 'percent' ? '0%' : '$0';
    }

    const num = Number(value);
    if (!Number.isFinite(num)) {
      return String(value);
    }

    if (format === 'percent') {
      return `${this.formatNumber(num, 2)}%`;
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(num);
  }

  private formatNumber(value: number, maximumFractionDigits: number): string {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits,
    }).format(value);
  }

  private getFilterLoadSignature(filters: any): string {
    return JSON.stringify({
      requestParams: filters?.requestParams || {},
      queryParams: filters?.queryParams || {},
      widgetId: filters?.widgetId || null,
      changedFilterKey: filters?.changedFilterKey || null,
      filterListener: filters?.filterListener || filters?.filterListner || null,
    });
  }
}
